// ================================================
// COGNITIVE LANES - Full Metadata
// ================================================
export const COGNITIVE_LANES = {
  vision_expansion: {
    id: 'vision_expansion',
    name: 'Vision & Expansion',
    color: '#00CED1',
    description: "You live in possibility. While others are managing today, you're already designing what comes next. You're most energized when you're expanding the edges of what's possible — new markets, new ideas, new directions that others haven't seen yet."
  },
  strategy_architecture: {
    id: 'strategy_architecture',
    name: 'Strategy & Architecture',
    color: '#1E3A8A',
    description: "You build the systems that make everything else work. You see the structure beneath the surface — how parts connect, where leverage lives, and how to design for outcomes months before they happen. You think in frameworks and your plans actually hold."
  },
  creative: {
    id: 'creative',
    name: 'Creative',
    color: '#9333EA',
    description: "You think in images, feelings, and concepts before you think in words. You're the person who sees what something could become and then makes it real. When you're in your lane, you produce work that surprises even yourself."
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    color: '#F97316',
    description: "You understand people. You know what moves them, what they respond to, and how to craft a message that lands exactly right. You're most alive when you're connecting something real to an audience that needs it."
  },
  operations: {
    id: 'operations',
    name: 'Operations',
    color: '#22C55E',
    description: "You're the person who makes things actually happen. When others see complexity you see a system waiting to be optimized. You find deep satisfaction in taking something messy and making it clean, repeatable, and reliable."
  },
  execution: {
    id: 'execution',
    name: 'Execution',
    color: '#14B8A6',
    description: "You deliver. When something needs to get done — really done, not almost done — you're the person it goes to. You have an almost physical need to close things out and you don't rest easy until the work is complete."
  },
  revenue: {
    id: 'revenue',
    name: 'Revenue',
    color: '#EAB308',
    description: "You understand value exchange at a fundamental level. You know how to find opportunity, build a case, and close. You're most energized when you're in motion — finding the next deal, the next partnership, the next growth lever."
  },
  relationships: {
    id: 'relationships',
    name: 'Relationships',
    color: '#EC4899',
    description: "You hold the organization together in ways that never show up on an org chart. You know who needs to talk to whom, when trust is at risk, and how to navigate complexity through connection rather than authority."
  },
  culture: {
    id: 'culture',
    name: 'Culture',
    color: '#A78BFA',
    description: "You feel the health of an organization like a change in air pressure. You know when something is off before anyone else names it and you know how to build environments where people do their best work."
  },
  finance_analysis: {
    id: 'finance_analysis',
    name: 'Finance & Analysis',
    color: '#64748B',
    description: "You see through numbers to truth. Where others see data you see story — what's really happening, what's at risk, and what needs to change. Your thinking keeps the organization honest and grounded in reality."
  }
};

// ================================================
// COGNITIVE LEVELS
// ================================================
export const COGNITIVE_LEVELS = {
  L1: {
    id: 'L1',
    name: 'L1 — DO',
    short: 'DO',
    description: 'Execute defined tasks with precision'
  },
  L2: {
    id: 'L2',
    name: 'L2 — RUN',
    short: 'RUN',
    description: 'Manage ongoing operations and processes'
  },
  L3: {
    id: 'L3',
    name: 'L3 — IMPROVE',
    short: 'IMPROVE',
    description: 'Optimize systems and solve problems'
  },
  L4: {
    id: 'L4',
    name: 'L4 — DECIDE & LEAD',
    short: 'DECIDE & LEAD',
    description: 'Set direction and make strategic choices'
  }
};

// ================================================
// LEVEL ASSESSMENT QUESTIONS
// ================================================
export const LEVEL_QUESTIONS = [
  {
    id: 1,
    question: "A complex problem lands on your desk that nobody has solved yet. Your first instinct is to:",
    options: [
      { id: 'L1', text: "Roll up my sleeves and work through it myself until it's solved" },
      { id: 'L2', text: "Figure out who owns this and make sure it gets to the right person" },
      { id: 'L3', text: "Understand why this keeps happening and fix the root cause" },
      { id: 'L4', text: "Decide whether this problem is even worth solving and set the direction for the team" }
    ]
  },
  {
    id: 2,
    question: "When you think about your highest value contribution to your organization, it's:",
    options: [
      { id: 'L1', text: "Completing work reliably and with precision" },
      { id: 'L2', text: "Keeping systems, people, and processes running smoothly" },
      { id: 'L3', text: "Finding and fixing what's not working before it becomes a problem" },
      { id: 'L4', text: "Setting direction, making consequential decisions, and leading others" }
    ]
  },
  {
    id: 3,
    question: "How do you feel when you're given a task with very clear instructions and defined parameters?",
    options: [
      { id: 'L1', text: "Comfortable — I do my best work when expectations are clear" },
      { id: 'L2', text: "Fine — I can work within structure and manage the process" },
      { id: 'L3', text: "Somewhat constrained — I always see ways to improve the approach" },
      { id: 'L4', text: "Frustrated — I'd rather define the parameters than receive them" }
    ]
  },
  {
    id: 4,
    question: "At the end of a strong week of work, what does 'winning' feel like for you?",
    options: [
      { id: 'L1', text: "I completed everything on my list with quality and nothing slipped" },
      { id: 'L2', text: "Everything ran smoothly, my team had what they needed, no fires" },
      { id: 'L3', text: "I solved something that had been broken and made the system better" },
      { id: 'L4', text: "I made a decision or set a direction that will compound over time" }
    ]
  }
];

// ================================================
// 40 LANE + LEVEL PROFILE DESCRIPTIONS
// ================================================
export const PROFILE_DESCRIPTIONS = {
  // VISION & EXPANSION
  'vision_expansion_L1': "You execute on expansive ideas with precision. While your instinct pulls toward the horizon, your strength right now is in making visionary concepts tangible through consistent delivery. You thrive when given a clear picture of what could be and the space to build toward it. What drains you is work that feels small or disconnected from any larger purpose.",
  
  'vision_expansion_L2': "You keep possibility in motion. Your role is maintaining the systems that enable expansion — keeping the gears turning on initiatives that push boundaries. You're at your best when you're running operations for growth-focused work, not maintenance. What drains you is managing processes that feel like they're standing still.",
  
  'vision_expansion_L3': "You optimize for expansion. You see the gaps between where the organization is and where it could be, and you work to close them systematically. You're most energized when improving the machinery of growth itself — making expansion more efficient, more sustainable, more ambitious. What drains you is fixing problems that feel like they don't matter to the bigger picture.",
  
  'vision_expansion_L4': "You're the person who sees where the organization needs to go before anyone else can name it. At your best you're setting direction that others build toward — expanding into new territory, opening new lanes of possibility, and making consequential choices that compound over time. You thrive when you have latitude and access to information; you wilt when you're managing instead of visioning. The organizations that understand how to use you become remarkable.",
  
  // STRATEGY & ARCHITECTURE
  'strategy_architecture_L1': "You execute strategic work with care and precision. You understand that the blueprints matter, and you take pride in building exactly what the architecture calls for. You thrive when the plan is clear and well-designed. What drains you is chaos disguised as flexibility, or being asked to build without understanding why.",
  
  'strategy_architecture_L2': "You run strategic systems. Your role is keeping the frameworks operational — ensuring that the structures designed by others continue to function as intended. You're at your best when you're maintaining the integrity of well-designed systems. What drains you is running processes that were never properly architected in the first place.",
  
  'strategy_architecture_L3': "You refine the architecture. You see where systems break down, where leverage is lost, where the structure could be tighter. You're most energized when you're redesigning something that almost works into something that works beautifully. What drains you is tinkering with systems that need to be replaced, not improved.",
  
  'strategy_architecture_L4': "You design the systems that make organizations succeed. At your best you're setting the structural foundations that everything else builds upon — frameworks, processes, decision architectures that shape how hundreds of choices get made. You thrive with time to think and model; you wilt when forced into reactive mode. The organizations that let you build become systematically excellent.",
  
  // CREATIVE
  'creative_L1': "You bring creative visions to life through craft. Your hands and mind work together to execute on ideas that others only imagine. You thrive when given a clear creative direction and the freedom to execute with excellence. What drains you is being treated as a production machine rather than a craftsperson.",
  
  'creative_L2': "You keep creative production flowing. Your role is maintaining the rhythm of creative work — ensuring projects move forward, feedback loops close, and creative energy stays focused. You're at your best when you're orchestrating creative work without constraining it. What drains you is process that kills the spark it's meant to protect.",
  
  'creative_L3': "You elevate creative output. You see where the work could be better, where the process could serve the creativity more fully, where constraints could become advantages. You're most energized when you're making something good into something remarkable. What drains you is optimizing for efficiency at the expense of quality.",
  
  'creative_L4': "You set the creative direction that defines how organizations express themselves. At your best you're making the calls that shape brand, voice, visual identity, and creative culture — deciding what beautiful means and holding the line on it. You thrive with creative authority and organizational trust; you wilt in environments that treat creativity as decoration. The organizations that empower you become distinctive.",
  
  // MARKETING
  'marketing_L1': "You execute marketing with empathy and precision. You understand that every touchpoint matters, and you take pride in crafting messages that actually reach people. You thrive when you have clarity on the audience and freedom in the execution. What drains you is marketing work disconnected from real humans.",
  
  'marketing_L2': "You keep marketing in motion. Your role is maintaining the cadence of campaigns, content, and communication — ensuring the marketing engine runs without sputtering. You're at your best when you're orchestrating the many pieces of marketing work into a coherent flow. What drains you is running a machine that isn't connected to results.",
  
  'marketing_L3': "You optimize how organizations connect with their audiences. You see what's landing and what isn't, what could work better, what the data is trying to tell you. You're most energized when you're making the marketing work harder, smarter, more precisely. What drains you is improving metrics that don't matter.",
  
  'marketing_L4': "You decide how organizations show up in the world. At your best you're setting marketing strategy that builds real connection — deciding who to talk to, what to say, and how to say it in ways that compound over time. You thrive with market insight and creative partnership; you wilt when separated from the customer. The organizations that listen to you grow.",
  
  // OPERATIONS
  'operations_L1': "You execute operational work with reliability and care. You understand that the system depends on each part working correctly, and you take pride in being the part that never fails. You thrive when the process is clear and the stakes are real. What drains you is work that doesn't seem to matter or processes that exist for no reason.",
  
  'operations_L2': "You keep operations running. Your role is maintaining the systems that keep the organization functional — ensuring nothing falls through, nothing breaks down, nothing surprises anyone who shouldn't be surprised. You're at your best when everything is running smoothly because of you. What drains you is chaos that could have been prevented.",
  
  'operations_L3': "You make operations better. You see inefficiency like a splinter under the skin — you can't leave it alone until it's fixed. You're most energized when you're redesigning something messy into something elegant and reliable. What drains you is fixing the same problems over and over because no one addresses root causes.",
  
  'operations_L4': "You design operational excellence into organizations. At your best you're setting the systems and standards that make scaling possible — deciding how things work, how quality is maintained, how complexity is managed. You thrive with authority over process; you wilt when you see problems you can't fix. The organizations that empower you become operationally remarkable.",
  
  // EXECUTION
  'execution_L1': "You close things out. You're the person who takes a task and finishes it — completely, correctly, on time. You thrive when you have a clear list and the autonomy to work through it. What drains you is ambiguity, moving targets, or work that never seems to actually end.",
  
  'execution_L2': "You keep execution moving across teams. Your role is ensuring that work gets done across multiple streams — tracking, unblocking, and maintaining momentum. You're at your best when you're the reason nothing stalls. What drains you is managing work that no one cares about completing.",
  
  'execution_L3': "You make execution faster and more reliable. You see where work gets stuck, where effort is wasted, where the path from start to finish could be shorter. You're most energized when you're removing friction from delivery. What drains you is improving processes for work that shouldn't exist.",
  
  'execution_L4': "You set the pace and priority for organizational execution. At your best you're deciding what gets done and in what order — making the calls that turn strategy into completed work. You thrive with clear authority and accountability; you wilt in organizations that can't commit. The organizations that follow your lead ship.",
  
  // REVENUE
  'revenue_L1': "You execute revenue work with consistency and skill. You understand the art and science of value exchange, and you take pride in closing what needs to close. You thrive when you have good leads and the tools to work them. What drains you is chasing deals that were never real or selling things you don't believe in.",
  
  'revenue_L2': "You keep the revenue engine running. Your role is maintaining the pipeline, the process, the cadence of deals and renewals that keep the business alive. You're at your best when the machine is humming and the numbers are climbing. What drains you is running a process that's structurally broken.",
  
  'revenue_L3': "You optimize how organizations generate revenue. You see where deals are lost, where value is left on the table, where the process could be tighter. You're most energized when you're making the revenue motion more effective. What drains you is optimizing tactics when the strategy is wrong.",
  
  'revenue_L4': "You set revenue strategy for organizations. At your best you're deciding how to grow — which markets, which products, which partnerships, which bets to make. You thrive with P&L authority and market access; you wilt when growth is constrained by forces outside your control. The organizations that follow your lead scale.",
  
  // RELATIONSHIPS
  'relationships_L1': "You build connection through consistent care. You understand that relationships are built in small moments, and you take pride in being someone people can count on. You thrive when you have time for people and trust in the relationships you're building. What drains you is being forced to treat relationships as transactions.",
  
  'relationships_L2': "You maintain the relationship fabric of organizations. Your role is keeping connections healthy — ensuring communication flows, conflicts resolve, and trust stays intact. You're at your best when you're the reason people work well together. What drains you is managing relationships in environments that don't value them.",
  
  'relationships_L3': "You strengthen how organizations relate internally and externally. You see where trust is weak, where communication breaks down, where relationships need repair. You're most energized when you're making connections that weren't working start to work. What drains you is fixing relationship problems that are symptoms of deeper dysfunction.",
  
  'relationships_L4': "You shape the relational architecture of organizations. At your best you're deciding who needs to connect with whom, where trust needs to be built, how the organization holds together through change. You thrive with social capital and positional authority; you wilt when forced to navigate politics you didn't create. The organizations that trust you become cohesive.",
  
  // CULTURE
  'culture_L1': "You contribute to culture through how you show up. You understand that culture is built in daily choices, and you take pride in embodying the environment you want to create. You thrive when the culture around you reflects your values. What drains you is working in environments that ask you to be someone you're not.",
  
  'culture_L2': "You maintain cultural health. Your role is keeping the environment functional — ensuring norms are upheld, values are lived, and the culture doesn't drift. You're at your best when you're the reason people feel good coming to work. What drains you is maintaining a culture you don't believe in.",
  
  'culture_L3': "You evolve organizational culture. You see where the environment is off, where norms need to shift, where the culture could better serve the people inside it. You're most energized when you're making a good culture into a great one. What drains you is trying to improve culture without the authority to change what creates it.",
  
  'culture_L4': "You define what it means to work in an organization. At your best you're setting the tone, values, and norms that shape how everyone experiences their work — deciding what behavior is celebrated and what isn't tolerated. You thrive with senior visibility and organizational trust; you wilt when culture is treated as HR's problem. The organizations that follow your lead become places people want to be.",
  
  // FINANCE & ANALYSIS
  'finance_analysis_L1': "You execute analytical work with precision and integrity. You understand that the numbers have to be right, and you take pride in producing work that can be trusted. You thrive when you have clean data and clear requirements. What drains you is being asked to make bad numbers look good.",
  
  'finance_analysis_L2': "You keep the analytical engine running. Your role is maintaining the flow of information that enables decisions — ensuring reports are accurate, dashboards are current, and the organization sees itself clearly. You're at your best when you're the reason leaders have the numbers they need. What drains you is running analyses that no one uses.",
  
  'finance_analysis_L3': "You make organizations smarter about their own performance. You see where measurement is weak, where insights are missed, where the data could tell a clearer story. You're most energized when you're improving how organizations understand themselves. What drains you is optimizing reports for audiences who don't want the truth.",
  
  'finance_analysis_L4': "You ground organizational decisions in reality. At your best you're the voice that says what the numbers actually mean — deciding what's real, what's at risk, and what needs to change. You thrive with board access and decision authority; you wilt when analysis is ignored in favor of intuition. The organizations that listen to you make better bets."
};

// ================================================
// INDUSTRIES LIST
// ================================================
export const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance & Banking',
  'Education',
  'Retail & E-commerce',
  'Manufacturing',
  'Media & Entertainment',
  'Professional Services',
  'Real Estate',
  'Non-Profit',
  'Government',
  'Energy & Utilities',
  'Transportation & Logistics',
  'Hospitality',
  'Other'
];

// ================================================
// HELPER FUNCTIONS
// ================================================
export const getLaneById = (id) => COGNITIVE_LANES[id] || null;
export const getLevelById = (id) => COGNITIVE_LEVELS[id] || null;
export const getProfileDescription = (laneId, levelId) => {
  const key = `${laneId}_${levelId}`;
  return PROFILE_DESCRIPTIONS[key] || 'Profile description not available.';
};

export const calculateLevelFromAnswers = (answers) => {
  const counts = { L1: 0, L2: 0, L3: 0, L4: 0 };
  answers.forEach(answer => {
    if (counts[answer] !== undefined) {
      counts[answer]++;
    }
  });
  return Object.entries(counts).reduce((a, b) => a[1] > b[1] ? a : b)[0];
};

export const calculateCognitiveSignature = (primaryLane, supportingLanes, level) => {
  // Base scores by level
  const levelScores = {
    L1: { strategic: 30, execution: 85, leadership: 25 },
    L2: { strategic: 45, execution: 75, leadership: 40 },
    L3: { strategic: 65, execution: 60, leadership: 55 },
    L4: { strategic: 85, execution: 45, leadership: 85 }
  };
  
  const strategicLanes = ['vision_expansion', 'strategy_architecture'];
  const executionLanes = ['execution', 'operations'];
  const collaborativeLanes = ['relationships', 'culture', 'marketing'];
  
  const base = levelScores[level] || levelScores.L2;
  
  // Strategic Reach
  let strategicReach = base.strategic;
  if (strategicLanes.includes(primaryLane)) strategicReach += 15;
  
  // Execution Orientation
  let executionOrientation = base.execution;
  if (executionLanes.includes(primaryLane)) executionOrientation += 15;
  
  // Collaborative Capacity
  let collaborativeCapacity = 50 + (supportingLanes?.length || 0) * 10;
  if (collaborativeLanes.includes(primaryLane)) collaborativeCapacity += 15;
  supportingLanes?.forEach(sl => {
    if (collaborativeLanes.includes(sl)) collaborativeCapacity += 5;
  });
  
  // Leadership Bandwidth
  let leadershipBandwidth = base.leadership;
  if (primaryLane === 'vision_expansion') leadershipBandwidth += 10;
  
  return {
    strategicReach: Math.min(100, strategicReach),
    executionOrientation: Math.min(100, executionOrientation),
    collaborativeCapacity: Math.min(100, collaborativeCapacity),
    leadershipBandwidth: Math.min(100, leadershipBandwidth)
  };
};
